(() => {
  const initializeCatalog = (section) => {
    if (!section || section.dataset.catalogControllerReady === 'true') return;
    section.dataset.catalogControllerReady = 'true';
    const lifecycleController = new AbortController();
    const { signal: lifecycleSignal } = lifecycleController;

    const grid = section.querySelector('[data-asc-catalog-grid]');
    if (!grid) return;

    const toolbar = section.querySelector('.asc-catalog__toolbar');
    const drawer = section.querySelector('[data-filter-drawer]');
    const groupsRoot = section.querySelector('[data-filter-groups]');
    const filterCount = section.querySelector('[data-filter-count]');
    const toolbarCount = section.querySelector('[data-toolbar-count]');
    const toolbarCountMobile = toolbarCount?.querySelector('[data-toolbar-count-mobile]');
    const toolbarCountDesktop = toolbarCount?.querySelector('[data-toolbar-count-desktop]');
    const toolbarFilterLabel = section.querySelector('[data-toolbar-filter-label]');
    const clearButton = section.querySelector('[data-filter-clear]');
    const titleCount = section.querySelector('.asc-catalog__count');
    const noResults = section.querySelector('[data-no-results]');
    const loadSentinel = section.querySelector('[data-catalog-load-sentinel]');
    const stickyHeader = document.querySelector('.section-header');
    const products = Array.from(grid.querySelectorAll('.asc-catalog-product'));
    const totalProducts = Number(section.dataset.totalProducts) || products.length;
    const showCollectionFilter = section.dataset.isAll === 'true';
    const selectedCollectionOptions = showCollectionFilter
      ? Array.from(section.querySelectorAll('[data-selected-collection-filter]')).map((item) => ({
        label: item.dataset.label || '',
        value: item.dataset.value || '',
        values: [item.dataset.value || '']
      })).filter((option) => option.label && option.value)
      : [];
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const quickViewModal = section.querySelector('[data-quick-view-modal]');
    const quickViewDialog = quickViewModal?.querySelector('.asc-quick-view__dialog');
    const quickViewBody = quickViewModal?.querySelector('[data-quick-view-body]');
    const quickViewStatus = quickViewModal?.querySelector('[data-quick-view-status]');
    const quickViewContent = quickViewModal?.querySelector('[data-quick-view-content]');
    const loadedScriptSources = new Set(Array.from(document.scripts).map((script) => script.src).filter(Boolean));
    let stickyFrame = null;
    let currentStickyOffset = -1;
    let quickViewTrigger = null;
    let quickViewRequest = null;
    let quickViewRequestId = 0;
    let quickViewCloseTimer = null;
    let stickyHeaderResizeObserver = null;
    let stickyHeaderMutationObserver = null;
    let productPageObserver = null;
    let productPageRequest = null;
    let productPageFrame = null;
    let nextPageUrl = grid.dataset.nextPageUrl || '';

    const QUICK_VIEW_MIN_LOADING_TIME = 150;
    const QUICK_VIEW_ASSET_TIMEOUT = 1200;

    const wait = (duration) => new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });

    const waitForNextFrame = () => new Promise((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });

    const waitWithTimeout = (promise, timeout = QUICK_VIEW_ASSET_TIMEOUT) => {
      return Promise.race([promise, wait(timeout)]);
    };

    const waitForQuickViewStyles = async (container) => {
      const stylesheets = Array.from(container.querySelectorAll('link[rel=\"stylesheet\"]'));

      await Promise.all(stylesheets.map((stylesheet) => {
        if (stylesheet.sheet) return Promise.resolve();

        return waitWithTimeout(new Promise((resolve) => {
          stylesheet.addEventListener('load', resolve, { once: true });
          stylesheet.addEventListener('error', resolve, { once: true });
        }));
      }));
    };

    const waitForQuickViewImages = async (container) => {
      const images = Array.from(container.querySelectorAll('img'));
      const priorityImages = images.filter((image, index) => {
        return index < 3 || image.loading !== 'lazy' || image.fetchPriority === 'high';
      });

      await Promise.all(priorityImages.map((image) => {
        const waitForLoad = image.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });

        return waitWithTimeout(waitForLoad.then(async () => {
          if (typeof image.decode === 'function') {
            try {
              await image.decode();
            } catch {}
          }
        }));
      }));
    };

    const waitForQuickViewLayout = async (container) => {
      const productRoot = container.querySelector('[id^=\"AscPdp-\"]');
      if (!productRoot) return;

      let previousSize = '';
      let stableFrames = 0;

      for (let frame = 0; frame < 12; frame += 1) {
        await waitForNextFrame();

        const rect = productRoot.getBoundingClientRect();
        const currentSize = `${Math.round(rect.width)}x${Math.round(rect.height)}`;

        if (rect.width > 0 && rect.height > 0 && currentSize === previousSize) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }

        if (stableFrames >= 2) return;
        previousSize = currentSize;
      }
    };

    const waitForQuickViewPresentation = async (container) => {
      await waitForQuickViewLayout(container);
      await waitForNextFrame();
    };

    const syncToolbarStickyState = () => {
      if (!toolbar) return;

      const stickyTop = Number.parseFloat(window.getComputedStyle(toolbar).top) || 0;
      const toolbarRect = toolbar.getBoundingClientRect();
      const titleCountRect = titleCount?.getBoundingClientRect();
      const isStuck = !showCollectionFilter
        && Math.abs(toolbarRect.top - stickyTop) <= 2
        && (!titleCountRect || titleCountRect.bottom <= stickyTop);

      toolbar.classList.toggle('is-stuck', isStuck);
    };

    const setStickyOffset = () => {
      const headerStyle = stickyHeader ? window.getComputedStyle(stickyHeader) : null;
      const headerRect = stickyHeader?.getBoundingClientRect();
      const headerIsVisible = headerStyle
        && headerStyle.display !== 'none'
        && headerStyle.visibility !== 'hidden'
        && Number(headerStyle.opacity || 1) > 0
        && headerRect.height > 0
        && headerRect.bottom > 0;
      const headerBottom = headerIsVisible ? headerRect.bottom : 0;
      const nextOffset = Math.max(0, Math.min(240, Math.floor(headerBottom) - 1));

      if (nextOffset !== currentStickyOffset) {
        currentStickyOffset = nextOffset;
        section.style.setProperty('--asc-sticky-header-offset', `${nextOffset}px`);
      }

      syncToolbarStickyState();
    };

    const queueStickyOffset = () => {
      if (stickyFrame) return;

      stickyFrame = requestAnimationFrame(() => {
        stickyFrame = null;
        setStickyOffset();
      });
    };

    const filterGroups = [
      {
        key: 'sort',
        label: 'SORT BY',
        type: 'radio',
        open: true,
        options: [
          { label: 'Featured', value: 'featured' },
          { label: 'Most Relevant', value: 'most-relevant' },
          { label: 'Best Selling', value: 'best-selling' },
          { label: 'Alphabetically, A-Z', value: 'title-ascending' },
          { label: 'Alphabetically, Z-A', value: 'title-descending' },
          { label: 'Price, Low to High', value: 'price-ascending' },
          { label: 'Price, High to Low', value: 'price-descending' },
          { label: 'Date, Old to New', value: 'date-ascending' },
          { label: 'Date, New to Old', value: 'date-descending' }
        ]
      },
      {
        key: 'set-style',
        label: 'SET STYLE',
        type: 'checkbox',
        options: [
          { label: 'Single', values: ['single'] },
          { label: '2-Piece Set', values: ['2-piece-set', 'two-piece-set', '2-piece'] },
          { label: '3-Piece Set', values: ['3-piece-set', 'three-piece-set', '3-piece', 'trilogy'] },
          { label: 'Gallery Wall Set', values: ['gallery-wall-set', 'gallery-wall'] }
        ]
      },
      {
        key: 'theme',
        label: 'THEME',
        type: 'checkbox',
        options: ['Abstract', 'Animal', 'Architectural', 'Automotive', 'Black & White', 'Cinema', 'Dark / Moody', 'Fantasy', 'Fashion', 'Figurative', 'Floral', 'Food & Drink', 'Graffiti', 'Kids', 'Luxury', 'Minimalist', 'Modern', 'Motivational', 'Music', 'Nature', 'Pop Art', 'Retro / Vintage', 'Space', 'Sports', 'Statement Pieces', 'Street Art', 'Surreal', 'Typography', 'Urban'].map((label) => ({ label }))
      },
      {
        key: 'room-type',
        label: 'ROOM TYPE',
        type: 'checkbox',
        options: ['Living Room', 'Office', 'Bedroom', 'Lounge', 'Dining Room', 'Foyer', 'Hallway', 'Home Bar', 'Game Room', 'Study', 'Bathroom', 'Home Gym', 'Media Room', 'Basement', 'Conference Room', 'Waiting Room', 'Man Cave', 'Kitchen', 'Kids Room'].map((label) => ({ label }))
      },
      {
        key: 'mood',
        label: 'MOOD',
        type: 'checkbox',
        options: ['Bold', 'Calm', 'Dark', 'Luxurious', 'Motivational', 'Minimal', 'Playful'].map((label) => ({ label }))
      },
      {
        key: 'orientation',
        label: 'ORIENTATION',
        type: 'checkbox',
        options: ['Vertical', 'Horizontal', 'Panoramic', 'Square'].map((label) => ({ label }))
      },
      {
        key: 'sale',
        label: 'SALE',
        type: 'checkbox',
        options: [
          { label: 'On Sale', value: 'on-sale', special: 'sale' },
          { label: 'New Arrivals', value: 'new-arrivals', special: 'new' },
          { label: 'Best Sellers', value: 'best-sellers', special: 'best' }
        ]
      },
      {
        key: 'collection',
        label: 'COLLECTION',
        type: 'checkbox',
        options: []
      },
      {
        key: 'colors',
        label: 'COLORS',
        type: 'checkbox',
        options: ['Black', 'White', 'Grey', 'Beige', 'Brown', 'Bronze', 'Blue', 'Green', 'Red', 'Pink', 'Purple', 'Orange', 'Yellow', 'Gold', 'Silver', 'Neutral', 'Multicolor'].map((label) => ({ label }))
      }
    ];

    const activeFilterGroups = filterGroups.filter((group) => showCollectionFilter || group.key !== 'collection');
    const collectionGroup = activeFilterGroups.find((group) => group.key === 'collection');
    const collectionOptionsLocked = Boolean(collectionGroup && selectedCollectionOptions.length);

    if (collectionOptionsLocked) {
      collectionGroup.options = selectedCollectionOptions;
    }

    const slugify = (value) => String(value || '')
      .toLowerCase()
      .replace(/&/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const normalizeFilterLabel = (groupKey, label) => {
      const cleanLabel = String(label || '').trim();
      if (groupKey === 'colors' && /^gray$/i.test(cleanLabel)) return 'Grey';
      return cleanLabel;
    };

    const normalizeFilterValue = (groupKey, value) => {
      const normalized = slugify(value);
      if (groupKey === 'colors' && normalized === 'gray') return 'grey';
      return normalized;
    };

    const colorSwatches = {
      black: '#050505',
      white: '#ffffff',
      gray: '#8b8b8b',
      grey: '#8b8b8b',
      beige: '#d8c8aa',
      brown: '#8a5732',
      bronze: '#b97935',
      blue: '#315f9d',
      'light-blue': '#9fb9d7',
      navy: '#1d2d4f',
      green: '#476f42',
      olive: '#6f6f3f',
      red: '#ef2f2f',
      pink: '#d99aad',
      purple: '#7354a5',
      orange: '#d67a2b',
      yellow: '#d8b83c',
      gold: '#c79b45',
      silver: '#b9b9b9',
      neutral: '#b7ad9d',
      cream: '#eee4cf',
      ivory: '#f4eedf',
      multicolor: 'linear-gradient(135deg, #050505 0 20%, #ef2f2f 20% 40%, #d67a2b 40% 60%, #315f9d 60% 80%, #ffffff 80% 100%)',
      'multi-color': 'linear-gradient(135deg, #050505 0 20%, #ef2f2f 20% 40%, #d67a2b 40% 60%, #315f9d 60% 80%, #ffffff 80% 100%)',
      'black-white': 'linear-gradient(135deg, #050505 0 50%, #ffffff 50% 100%)',
      'black-and-white': 'linear-gradient(135deg, #050505 0 50%, #ffffff 50% 100%)'
    };

    const getColorSwatch = (label) => {
      const key = slugify(label).replace(/-+/g, '-');
      return colorSwatches[key] || key || '#ffffff';
    };

    const filterValueAttribute = {
      'set-style': 'filterSetStyle',
      theme: 'filterTheme',
      'room-type': 'filterRoomType',
      mood: 'filterMood',
      orientation: 'filterOrientation',
      collection: 'filterCollection',
      colors: 'filterColors'
    };

    const splitFilterValues = (value) => String(value || '')
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean);

    activeFilterGroups.forEach((group) => {
      group.options.forEach((option) => {
        option.label = normalizeFilterLabel(group.key, option.label);
        option.value = option.value || normalizeFilterValue(group.key, option.label);
        option.value = normalizeFilterValue(group.key, option.value);
        option.values = (option.values || [option.value]).map((value) => normalizeFilterValue(group.key, value));
      });
    });

    const addDynamicOption = (group, rawLabel) => {
      if (group.key === 'collection' && collectionOptionsLocked) return;

      const label = normalizeFilterLabel(group.key, rawLabel);
      const value = normalizeFilterValue(group.key, label);
      if (!value || group.options.some((option) => option.value === value)) return;

      group.options.push({ label, value, values: [value] });
    };

    const prepareProductData = (product, productIndex) => {
      const groups = {};

      activeFilterGroups.forEach((group) => {
        const attribute = filterValueAttribute[group.key];
        const rawLabels = attribute ? splitFilterValues(product.dataset[attribute]) : [];
        const labels = rawLabels.map((label) => normalizeFilterLabel(group.key, label));
        const values = labels.map((label) => normalizeFilterValue(group.key, label)).filter(Boolean);

        groups[group.key] = new Set(values);
        labels.forEach((label) => addDynamicOption(group, label));
      });

      product.catalogData = {
        productIndex,
        index: Number(product.dataset.index) || 0,
        price: Number(product.dataset.price) || 0,
        title: product.dataset.title || '',
        date: Number(product.dataset.date) || 0,
        sale: product.dataset.sale === 'true',
        best: product.dataset.best === 'true',
        new: product.dataset.new === 'true',
        groups,
        tokens: new Set((product.dataset.tags || '').split(/\s+/).filter(Boolean))
      };
    };

    products.forEach(prepareProductData);

    const emptyState = () => ({
      sort: 'featured',
      filters: {}
    });

    const cloneState = (source) => JSON.parse(JSON.stringify(source));
    let state = emptyState();
    let draft = cloneState(state);

    const getSelected = (filters, key) => filters[key] || [];

    const hasActiveFilters = (filters) => Object.values(filters).some((values) => values && values.length);

    const getSelectionCount = (filterState) => {
      const filterCountValue = Object.values(filterState.filters || {}).reduce((total, values) => {
        return total + (Array.isArray(values) ? values.length : 0);
      }, 0);

      return filterCountValue + (filterState.sort && filterState.sort !== 'featured' ? 1 : 0);
    };

    const updateClearButton = () => {
      if (!clearButton) return;

      const selectionCount = getSelectionCount(draft);
      const isActive = selectionCount > 0;
      clearButton.disabled = !isActive;
      clearButton.classList.toggle('is-active', isActive);
      clearButton.textContent = isActive ? `Clear All (${selectionCount})` : 'Clear All';
      clearButton.setAttribute('aria-disabled', isActive ? 'false' : 'true');
    };

    const optionMatchesProduct = (product, group, option) => {
      const data = product.catalogData;

      if (option.special === 'sale') return data.sale;
      if (option.special === 'new') return data.new;
      if (option.special === 'best') return data.best;

      if (data.groups[group.key]?.has(option.value)) return true;

      return option.values.some((value) => data.tokens.has(value));
    };

    let allProductIndexes = new Set();
    const optionProductIndexes = new Map();

    const rebuildProductIndexes = () => {
      allProductIndexes = new Set(products.map((product) => product.catalogData.productIndex));
      optionProductIndexes.clear();

      activeFilterGroups.forEach((group) => {
        if (group.key === 'sort') return;

        group.options.forEach((option) => {
          optionProductIndexes.set(
            `${group.key}:${option.value}`,
            new Set(
              products
                .filter((product) => optionMatchesProduct(product, group, option))
                .map((product) => product.catalogData.productIndex)
            )
          );
        });
      });
    };

    rebuildProductIndexes();

    const getMatchingProductIndexes = (filters, overrideKey, overrideValues) => {
      let matchingIndexes = null;

      activeFilterGroups.forEach((group) => {
        if (group.key === 'sort') return;

        const selected = group.key === overrideKey ? overrideValues : getSelected(filters, group.key);
        if (!selected.length) return;

        const groupIndexes = new Set();
        selected.forEach((value) => {
          optionProductIndexes.get(`${group.key}:${value}`)?.forEach((productIndex) => {
            groupIndexes.add(productIndex);
          });
        });

        if (matchingIndexes === null) {
          matchingIndexes = groupIndexes;
          return;
        }

        const smallerSet = matchingIndexes.size <= groupIndexes.size ? matchingIndexes : groupIndexes;
        const largerSet = smallerSet === matchingIndexes ? groupIndexes : matchingIndexes;
        matchingIndexes = new Set([...smallerSet].filter((productIndex) => largerSet.has(productIndex)));
      });

      return matchingIndexes || allProductIndexes;
    };

    const getVisibleProducts = (filters) => {
      const matchingIndexes = getMatchingProductIndexes(filters);
      return products.filter((product) => matchingIndexes.has(product.catalogData.productIndex));
    };

    const getOptionCount = (group, option) => {
      return getMatchingProductIndexes(draft.filters, group.key, [option.value]).size;
    };

    let appliedSort = 'featured';
    const sortProducts = () => {
      if (state.sort === appliedSort) return;

      const sorted = [...products].sort((a, b) => {
        const first = a.catalogData;
        const second = b.catalogData;

        if (state.sort === 'best-selling') return Number(second.best) - Number(first.best) || first.index - second.index;
        if (state.sort === 'title-ascending') return first.title.localeCompare(second.title);
        if (state.sort === 'title-descending') return second.title.localeCompare(first.title);
        if (state.sort === 'price-ascending') return first.price - second.price;
        if (state.sort === 'price-descending') return second.price - first.price;
        if (state.sort === 'date-ascending') return first.date - second.date;
        if (state.sort === 'date-descending') return second.date - first.date;

        return first.index - second.index;
      });

      sorted.forEach((product) => grid.appendChild(product));
      appliedSort = state.sort;
    };

    const updateToolbarState = (displayCount) => {
      const desktopCountLabel = `(${displayCount} products)`;
      const mobileCountLabel = `(${displayCount})`;

      if (toolbarCountDesktop) toolbarCountDesktop.textContent = desktopCountLabel;
      if (toolbarCountMobile) toolbarCountMobile.textContent = mobileCountLabel;
      if (toolbarCount && !toolbarCountDesktop && !toolbarCountMobile) toolbarCount.textContent = desktopCountLabel;

      if (toolbarFilterLabel) {
        const sortGroup = activeFilterGroups.find((group) => group.key === 'sort');
        const sortOption = sortGroup?.options.find((option) => option.value === state.sort);
        const hasCustomSort = state.sort && state.sort !== 'featured';
        toolbarFilterLabel.textContent = hasCustomSort && sortOption ? sortOption.label : 'Filter & Sort';
      }
    };

    let cardAlignmentFrame = 0;

    const alignDesktopCardRows = () => {
      products.forEach((product) => {
        const content = product.querySelector('.asc-catalog-card__content');
        if (!content) return;
        content.style.removeProperty('--asc-card-badge-offset');
        content.style.removeProperty('--asc-card-badge-gap');
        content.style.removeProperty('--asc-card-title-gap');
      });

      if (mobileQuery.matches) return;

      const visibleProducts = products.filter((product) => !product.hidden && product.offsetParent !== null);
      const rows = [];

      visibleProducts.forEach((product) => {
        const top = product.getBoundingClientRect().top;
        let row = rows.find((item) => Math.abs(item.top - top) < 2);

        if (!row) {
          row = { top, products: [] };
          rows.push(row);
        }

        row.products.push(product);
      });

      rows.forEach((row) => {
        const measurements = row.products.map((product) => {
          const badges = product.querySelector('.asc-catalog__badges--content');
          const badge = badges?.querySelector('.asc-catalog__badge');
          const title = product.querySelector('.asc-catalog-card__title');

          return {
            product,
            hasBadge: Boolean(badge),
            badgeHeight: badge ? Math.ceil(badge.getBoundingClientRect().height) : 0,
            titleHeight: title ? Math.ceil(title.getBoundingClientRect().height) : 0,
          };
        });

        const maxBadgeHeight = Math.max(0, ...measurements.map((item) => item.badgeHeight));
        const maxTitleHeight = Math.max(0, ...measurements.map((item) => item.titleHeight));

        measurements.forEach(({ product, hasBadge, badgeHeight, titleHeight }) => {
          const content = product.querySelector('.asc-catalog-card__content');
          if (!content) return;

          const badgeOffset = !hasBadge && maxBadgeHeight > 0 ? maxBadgeHeight + 10 : 0;
          const badgeGap = hasBadge ? 10 + Math.max(0, maxBadgeHeight - badgeHeight) : 10;
          const titleGap = 10 + Math.max(0, maxTitleHeight - titleHeight);

          content.style.setProperty('--asc-card-badge-offset', `${badgeOffset}px`);
          content.style.setProperty('--asc-card-badge-gap', `${badgeGap}px`);
          content.style.setProperty('--asc-card-title-gap', `${titleGap}px`);
        });
      });
    };

    const queueDesktopCardAlignment = () => {
      window.cancelAnimationFrame(cardAlignmentFrame);
      cardAlignmentFrame = window.requestAnimationFrame(() => {
        cardAlignmentFrame = window.requestAnimationFrame(alignDesktopCardRows);
      });
    };

    const updateProducts = () => {
      const visibleProducts = getVisibleProducts(state.filters);
      const visibleSet = new Set(visibleProducts);

      products.forEach((product) => {
        product.hidden = !visibleSet.has(product);
      });

      sortProducts();

      const displayCount = hasActiveFilters(state.filters) ? visibleProducts.length : totalProducts;
      const countLabel = `(${displayCount} products)`;
      if (filterCount) filterCount.textContent = countLabel;
      updateToolbarState(displayCount);
      if (titleCount) titleCount.textContent = `(${displayCount})`;
      if (noResults) noResults.hidden = visibleProducts.length > 0;

      grid.classList.add('is-updating');
      window.requestAnimationFrame(() => {
        grid.getBoundingClientRect();
        grid.classList.remove('is-updating');
        queueDesktopCardAlignment();
      });
    };

    const updateOptionStates = () => {
      const draftCount = hasActiveFilters(draft.filters) ? getVisibleProducts(draft.filters).length : totalProducts;
      if (filterCount) filterCount.textContent = `(${draftCount} products)`;

      activeFilterGroups.forEach((group) => {
        group.options.forEach((option) => {
          const input = groupsRoot.querySelector(`[data-filter-input="${group.key}:${option.value}"]`);
          if (!input) return;

          const label = input.closest('.asc-catalog-filter__option');
          const countNode = label.querySelector('[data-option-count]');
          const selected = group.key === 'sort' ? draft.sort === option.value : getSelected(draft.filters, group.key).includes(option.value);
          const count = group.key === 'sort' ? null : getOptionCount(group, option);
          const disabled = group.key !== 'sort' && count === 0 && !selected;

          input.checked = selected;
          input.disabled = disabled;
          label.classList.toggle('is-selected', selected);
          label.classList.toggle('is-disabled', disabled);

          if (countNode) countNode.textContent = group.key === 'sort' ? '' : ` (${count})`;
        });
      });

      updateClearButton();
    };

    const renderFilterGroups = () => {
      const arrow = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none" aria-hidden="true"><path d="M14.6332 0.683417L7.31658 8L0 0.683418L0.683417 4.46964e-07L7.31658 6.63317L13.9497 -1.32926e-07L14.6332 0.683417Z" fill="#1F1F1F"/></svg>';
      groupsRoot.replaceChildren();

      activeFilterGroups.forEach((group) => {
        const isColorGroup = group.key === 'colors';
        const groupNode = document.createElement('div');
        groupNode.className = `asc-catalog-filter${isColorGroup ? ' asc-catalog-filter--colors' : ''}${group.open ? ' is-open' : ''}`;

        const button = document.createElement('button');
        button.className = 'asc-catalog-filter__toggle';
        button.type = 'button';
        button.setAttribute('aria-expanded', group.open ? 'true' : 'false');
        button.innerHTML = `<span>${group.label}</span>${arrow}`;

        const optionsNode = document.createElement('div');
        optionsNode.className = 'asc-catalog-filter__options';

        group.options.forEach((option) => {
          const label = document.createElement('label');
          label.className = `asc-catalog-filter__option asc-catalog-filter__option--${group.type}${isColorGroup ? ' asc-catalog-filter__option--color' : ''}`;
          label.title = option.label;

          const input = document.createElement('input');
          input.className = 'asc-catalog-filter__input';
          input.type = group.type;
          input.name = `asc-filter-${group.key}`;
          input.value = option.value;
          input.dataset.filterInput = `${group.key}:${option.value}`;
          input.setAttribute('aria-label', option.label);

          const mark = document.createElement('span');
          mark.className = isColorGroup ? 'asc-catalog-filter__color-swatch' : 'asc-catalog-filter__mark';

          if (isColorGroup) {
            mark.style.setProperty('--asc-filter-color', getColorSwatch(option.label));
          }

          const text = document.createElement('span');
          text.className = 'asc-catalog-filter__text';
          text.textContent = option.label;

          const count = document.createElement('span');
          count.className = 'asc-catalog-filter__count';
          count.dataset.optionCount = '';

          label.append(input, mark, text, count);
          optionsNode.append(label);

          input.addEventListener('change', () => {
            if (input.disabled) return;

            if (group.key === 'sort') {
              draft.sort = option.value;
            } else {
              const selected = new Set(getSelected(draft.filters, group.key));
              input.checked ? selected.add(option.value) : selected.delete(option.value);
              draft.filters[group.key] = Array.from(selected);
              if (!draft.filters[group.key].length) delete draft.filters[group.key];
            }

            updateOptionStates();
            applyDraft();
          });
        });

        button.addEventListener('click', () => {
          groupNode.classList.toggle('is-open');
          button.setAttribute('aria-expanded', groupNode.classList.contains('is-open') ? 'true' : 'false');
        });

        groupNode.append(button, optionsNode);
        groupsRoot.append(groupNode);
      });
    };

    const appendProductPage = (nextGrid) => {
      const newProducts = Array.from(nextGrid.querySelectorAll('.asc-catalog-product'));
      if (!newProducts.length) return 0;

      const fragment = document.createDocumentFragment();
      newProducts.forEach((product) => {
        prepareProductData(product, products.length);
        products.push(product);
        fragment.appendChild(product);
      });

      grid.appendChild(fragment);
      rebuildProductIndexes();
      appliedSort = '';
      updateProducts();
      window.AscProductCards?.scan(grid);
      return newProducts.length;
    };

    const loadNextProductPage = () => {
      if (!nextPageUrl) return Promise.resolve(false);
      if (productPageRequest) return productPageRequest;

      const requestUrl = new URL(nextPageUrl, window.location.href);
      requestUrl.searchParams.set('section_id', section.dataset.sectionId);

      productPageRequest = fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: lifecycleSignal
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load catalog page ${response.status}`);
          return response.text();
        })
        .then((markup) => {
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          const nextGrid = documentFragment.querySelector('[data-asc-catalog-grid]');
          if (!nextGrid) {
            nextPageUrl = '';
            productPageObserver?.disconnect();
            return false;
          }

          nextPageUrl = nextGrid.dataset.nextPageUrl || '';
          grid.dataset.nextPageUrl = nextPageUrl;
          const appended = appendProductPage(nextGrid);
          if (!nextPageUrl) productPageObserver?.disconnect();
          return appended > 0;
        })
        .finally(() => {
          productPageRequest = null;
        });

      return productPageRequest;
    };

    const loadRemainingProducts = async () => {
      if (!nextPageUrl) return;
      drawer?.setAttribute('aria-busy', 'true');

      try {
        while (nextPageUrl) {
          const loaded = await loadNextProductPage();
          if (!loaded) break;
        }
      } catch (error) {
        if (error.name !== 'AbortError') return;
      } finally {
        drawer?.removeAttribute('aria-busy');
      }

      if (!nextPageUrl) {
        renderFilterGroups();
        updateOptionStates();
        updateProducts();
      }
    };


    const setQuickViewStatus = (type, productUrl) => {
      if (!quickViewStatus) return;

      if (type === 'loading') {
        quickViewContent?.classList.remove('is-ready');
        quickViewContent?.setAttribute('aria-hidden', 'true');
        quickViewStatus.innerHTML = '<span class="asc-quick-view__spinner" aria-hidden="true"></span><span class="visually-hidden">Loading product</span>';
        quickViewStatus.hidden = false;
        return;
      }

      if (type === 'error') {
        quickViewContent?.classList.remove('is-ready');
        quickViewContent?.setAttribute('aria-hidden', 'true');
        quickViewStatus.innerHTML = `<div><div>Unable to load this product.</div><a class="asc-quick-view__error-link" href="${productUrl}">View product page</a></div>`;
        quickViewStatus.hidden = false;
        return;
      }

      quickViewContent?.classList.add('is-ready');
      quickViewContent?.setAttribute('aria-hidden', 'false');
      quickViewStatus.hidden = true;
      quickViewStatus.replaceChildren();
    };

    const destroyQuickViewProduct = () => {
      if (!quickViewContent) return;
      const productRoot = quickViewContent.querySelector('[id^="AscPdp-"]');
      if (productRoot) productRoot.dispatchEvent(new CustomEvent('asc:pdp:destroy'));
      quickViewContent.classList.remove('is-ready');
      quickViewContent.setAttribute('aria-hidden', 'true');
      quickViewContent.replaceChildren();
    };

    const loadExternalScript = (source, sourceScript) => new Promise((resolve, reject) => {
      const absoluteSource = new URL(source, window.location.href).href;
      if (loadedScriptSources.has(absoluteSource)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      Array.from(sourceScript.attributes).forEach((attribute) => {
        if (attribute.name !== 'src' && attribute.name !== 'defer' && attribute.name !== 'async') {
          script.setAttribute(attribute.name, attribute.value);
        }
      });
      script.src = absoluteSource;
      script.async = false;
      script.addEventListener('load', () => {
        loadedScriptSources.add(absoluteSource);
        resolve();
      }, { once: true });
      script.addEventListener('error', () => reject(new Error(`Unable to load ${absoluteSource}`)), { once: true });
      document.head.appendChild(script);
    });

    const activateQuickViewScripts = async (container) => {
      const scripts = Array.from(container.querySelectorAll('script'));

      for (const sourceScript of scripts) {
        const source = sourceScript.getAttribute('src');
        if (source) {
          sourceScript.remove();

          if (/product-form(?:\.min)?\.js(?:[?#]|$)/i.test(source) || source.toLowerCase().includes('product-form')) {
            continue;
          }

          await loadExternalScript(source, sourceScript);
          continue;
        }

        const script = document.createElement('script');
        Array.from(sourceScript.attributes).forEach((attribute) => {
          script.setAttribute(attribute.name, attribute.value);
        });
        script.textContent = sourceScript.textContent;
        sourceScript.replaceWith(script);
      }
    };

    const fetchProductSection = (productUrl) => {
      return window.ascFetchConfiguredProductSection(productUrl, quickViewModal.id);
    };

    const getQuickViewCartRenderer = () => {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    };

    const getQuickViewCartSections = (cartRenderer) => {
      const sectionIds = [];

      if (cartRenderer && typeof cartRenderer.getSectionsToRender === 'function') {
        try {
          cartRenderer.getSectionsToRender().forEach((item) => {
            const sectionId = item && (item.section || item.id);
            if (sectionId) sectionIds.push(sectionId);
          });
        } catch (error) {
          console.warn('[Quick View] Unable to resolve cart sections.', error);
        }
      }

      if (document.querySelector('cart-drawer')) {
        sectionIds.push('cart-drawer', 'cart-icon-bubble');
      }

      return [...new Set(sectionIds.filter(Boolean))];
    };

    const updateQuickViewCartCount = (itemCount) => {
      const count = Number(itemCount || 0);

      document.querySelectorAll([
        '[data-cart-count]',
        '.cart-count-bubble span[aria-hidden="true"]',
        '.cart-count-bubble span:first-child',
        '.header__cart-count'
      ].join(',')).forEach((target) => {
        target.textContent = String(count);
      });

      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', count <= 0);
        bubble.hidden = count <= 0;
      });
    };

    const getQuickViewFormMessage = (form) => {
      let message = form.querySelector('[data-product-form-message]');

      if (!message) {
        message = document.createElement('p');
        message.className = 'asc-pdp__form-message';
        message.dataset.productFormMessage = '';
        message.hidden = true;
        form.appendChild(message);
      }

      return message;
    };

    const setQuickViewFormMessage = (form, messageText = '', isError = false) => {
      const message = getQuickViewFormMessage(form);
      message.textContent = messageText;
      message.classList.toggle('is-error', isError);
      message.hidden = !messageText;
    };

    const clearQuickViewCartEmptyState = (cartRenderer) => {
      const drawer = document.querySelector('cart-drawer');

      [cartRenderer, drawer].filter(Boolean).forEach((element) => {
        element.classList.remove('is-empty');
        element.querySelector?.('.drawer__inner')?.classList.remove('is-empty');
        element.querySelector?.('cart-drawer-items')?.classList.remove('is-empty');
      });
    };

    const getQuickViewSectionElement = (html, selector) => {
      if (typeof html !== 'string' || !html.trim()) return null;

      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
    };

    const requestQuickViewCartSections = async (sectionIds) => {
      const cartUrl = window.routes?.cart_url || '/cart';
      const ids = [...new Set(sectionIds.filter(Boolean))];

      if (!ids.length) return {};

      const response = await fetch(
        `${cartUrl}?sections=${encodeURIComponent(ids.join(','))}&sections_url=${encodeURIComponent(window.location.pathname)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error('Unable to load the updated cart sections.');
      }

      return response.json();
    };

    const renderQuickViewCartSectionsManually = (sections) => {
      const drawerHtml = sections?.['cart-drawer'];
      const bubbleHtml = sections?.['cart-icon-bubble'];

      if (drawerHtml) {
        const freshDrawer = getQuickViewSectionElement(drawerHtml, '#CartDrawer');
        const currentDrawer = document.querySelector('#CartDrawer');

        if (freshDrawer && currentDrawer) {
          currentDrawer.innerHTML = freshDrawer.innerHTML;
        }
      }

      if (bubbleHtml) {
        const freshBubble = getQuickViewSectionElement(bubbleHtml, '#cart-icon-bubble');
        const currentBubble = document.querySelector('#cart-icon-bubble');

        if (freshBubble && currentBubble) {
          currentBubble.innerHTML = freshBubble.innerHTML;
        }
      }
    };

    const renderQuickViewCartResult = async (cartRenderer, addResponse) => {
      const sectionIds = getQuickViewCartSections(cartRenderer);
      let sections = addResponse?.sections || {};

      if (
        document.querySelector('cart-drawer') &&
        typeof sections['cart-drawer'] !== 'string'
      ) {
        const freshSections = await requestQuickViewCartSections([
          ...sectionIds,
          'cart-drawer',
          'cart-icon-bubble'
        ]);

        sections = { ...sections, ...freshSections };
      }

      const renderState = {
        ...(addResponse || {}),
        sections
      };

      clearQuickViewCartEmptyState(cartRenderer);

      let renderedNatively = false;

      if (
        cartRenderer &&
        typeof cartRenderer.renderContents === 'function' &&
        (
          typeof sections['cart-drawer'] === 'string' ||
          !document.querySelector('cart-drawer')
        )
      ) {
        try {
          cartRenderer.setActiveElement?.(document.activeElement);
          cartRenderer.renderContents(renderState);
          renderedNatively = true;
        } catch (error) {
          console.error('[Quick View] Native cart render failed.', error);
        }
      }

      if (!renderedNatively) {
        renderQuickViewCartSectionsManually(sections);
      }

      clearQuickViewCartEmptyState(cartRenderer);
      window.requestAnimationFrame(() => clearQuickViewCartEmptyState(cartRenderer));

      let cart = null;

      try {
        const cartUrl = window.routes?.cart_url || '/cart';
        const response = await fetch(`${cartUrl}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (response.ok) cart = await response.json();
      } catch (error) {
        console.warn('[Quick View] Cart state refresh failed.', error);
      }

      if (cart) updateQuickViewCartCount(cart.item_count);

      const detail = {
        source: 'quick-view',
        cart,
        item: addResponse,
        productVariantId: addResponse?.variant_id || addResponse?.id
      };

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    };

    const bindQuickViewProductForm = (productRoot) => {
      const form = productRoot.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || form.dataset.quickViewSubmitBound === 'true') return;

      form.dataset.quickViewSubmitBound = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = form.querySelector('[type="submit"][name="add"], .asc-pdp__submit');
        if (!submitButton || submitButton.disabled || submitButton.dataset.loading === 'true') return;

        const submitText = submitButton.querySelector('[data-submit-text]');
        const originalText = submitText?.textContent || 'Add to cart';
        const cartRenderer = getQuickViewCartRenderer();
        const formData = new FormData(form);
        const sections = getQuickViewCartSections(cartRenderer);

        if (sections.length) {
          formData.set('sections', sections.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        setQuickViewFormMessage(form);
        submitButton.dataset.loading = 'true';
        submitButton.classList.add('is-loading');
        submitButton.setAttribute('aria-disabled', 'true');
        if (submitText) submitText.textContent = 'Adding...';

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData,
            credentials: 'same-origin'
          });

          const result = await response.json().catch(() => ({}));

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
          }

          await renderQuickViewCartResult(cartRenderer, result);

          if (submitText) submitText.textContent = 'Added';

          window.setTimeout(() => {
            closeQuickView();

            const drawer = document.querySelector('cart-drawer');
            const notification = document.querySelector('cart-notification');
            const activeCart = drawer || notification;

            if (activeCart && typeof activeCart.open === 'function') {
              window.setTimeout(() => activeCart.open(), 50);
            }
          }, 250);
        } catch (error) {
          console.error('[Quick View] Add to cart failed.', error);
          setQuickViewFormMessage(
            form,
            error.message || 'Unable to add this item to the cart.',
            true
          );
          if (submitText) submitText.textContent = originalText;
        } finally {
          if (submitButton.isConnected) {
            submitButton.dataset.loading = 'false';
            submitButton.classList.remove('is-loading');
            submitButton.removeAttribute('aria-disabled');
          }
        }
      }, true);
    };

    const initializeInjectedProduct = async () => {
      if (!quickViewContent) return;

      const productRoot = quickViewContent.querySelector('[id^="AscPdp-"]');
      if (!productRoot) throw new Error('Injected product root was not found');

      productRoot.dataset.updateUrl = 'false';
      productRoot.dataset.quickView = 'true';

      await activateQuickViewScripts(quickViewContent);
      bindQuickViewProductForm(productRoot);

      if (window.Shopify?.PaymentButton?.init) {
        window.Shopify.PaymentButton.init();
      }

      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        detail: { sectionId: productRoot.dataset.sectionId || '' }
      }));
    };

    const openQuickView = async (trigger) => {
      if (!quickViewModal || !quickViewContent || !quickViewDialog) return;

      const productUrl = trigger.dataset.productUrl;
      if (!productUrl) return;

      const loadingStartedAt = window.performance.now();

      if (quickViewCloseTimer) {
        window.clearTimeout(quickViewCloseTimer);
        quickViewCloseTimer = null;
      }

      quickViewRequestId += 1;
      const requestId = quickViewRequestId;
      quickViewRequest?.abort();
      quickViewRequest = new AbortController();
      quickViewTrigger = trigger;

      destroyQuickViewProduct();
      setQuickViewStatus('loading', productUrl);
      quickViewModal.hidden = false;
      quickViewModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('asc-quick-view-open');
      document.body.classList.add('asc-quick-view-open');
      window.requestAnimationFrame(() => quickViewModal.classList.add('is-open'));
      quickViewDialog.focus({ preventScroll: true });

      try {
        const markup = await fetchProductSection(productUrl);
        if (requestId !== quickViewRequestId || quickViewRequest.signal.aborted) return;

        quickViewContent.innerHTML = markup;
        await initializeInjectedProduct();
        await waitForQuickViewPresentation(quickViewContent);
        if (requestId !== quickViewRequestId || quickViewRequest.signal.aborted) return;

        const elapsedLoadingTime = window.performance.now() - loadingStartedAt;
        if (elapsedLoadingTime < QUICK_VIEW_MIN_LOADING_TIME) {
          await wait(QUICK_VIEW_MIN_LOADING_TIME - elapsedLoadingTime);
        }

        if (requestId !== quickViewRequestId || quickViewRequest.signal.aborted) return;

        setQuickViewStatus('ready', productUrl);
        quickViewBody?.scrollTo({ top: 0, behavior: 'auto' });

        const title = quickViewContent.querySelector('.asc-pdp__title')?.textContent?.trim();
        if (title) quickViewDialog.setAttribute('aria-label', `Quick View: ${title}`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Quick View]', error);
        destroyQuickViewProduct();
        setQuickViewStatus('error', productUrl);
      }
    };

    const closeQuickView = () => {
      if (!quickViewModal || quickViewModal.hidden) return;

      quickViewRequestId += 1;
      quickViewRequest?.abort();
      quickViewRequest = null;
      quickViewModal.classList.remove('is-open');
      quickViewModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('asc-quick-view-open');
      document.body.classList.remove('asc-quick-view-open');

      quickViewCloseTimer = window.setTimeout(() => {
        quickViewModal.hidden = true;
        destroyQuickViewProduct();
        setQuickViewStatus('ready', '');
        quickViewDialog?.setAttribute('aria-label', 'Quick View');
        quickViewCloseTimer = null;
      }, 220);

      if (quickViewTrigger && typeof quickViewTrigger.focus === 'function') {
        quickViewTrigger.focus({ preventScroll: true });
      }
      quickViewTrigger = null;
    };

    const trapQuickViewFocus = (event) => {
      if (event.key !== 'Tab' || !quickViewModal?.classList.contains('is-open')) return;

      const focusable = Array.from(quickViewModal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        quickViewDialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const openDrawer = () => {
      draft = cloneState(state);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('asc-catalog-filter-open');
      updateOptionStates();
      loadRemainingProducts();
    };

    const applyDraft = () => {
      state = cloneState(draft);
      updateProducts();
    };

    const closeDrawer = () => {
      if (drawer.classList.contains('is-open')) applyDraft();
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('asc-catalog-filter-open');
    };

    const setGrid = (button) => {
      if (!button) return;

      const group = button.closest('.grid-selector');
      if (!group) return;

      group.querySelectorAll('.grid-selector-wrap').forEach((item) => item.classList.remove('active'));
      button.classList.add('active');
      grid.style.setProperty('--asc-catalog-columns', button.dataset.grid);
      queueDesktopCardAlignment();
    };

    const getGridButtonForScope = (scope) => {
      const selector = scope === 'mobile' ? '.for_mobile_grid' : '.for_desktop_grid';
      const defaultColumns = scope === 'mobile' ? grid.dataset.mobileColumns || '2' : grid.dataset.desktopColumns || '3';

      return section.querySelector(`${selector} .grid-selector-wrap.active`)
        || section.querySelector(`${selector} .grid-selector-wrap[data-grid="${defaultColumns}"]`)
        || section.querySelector(`${selector} .grid-selector-wrap`);
    };

    const applyResponsiveGrid = () => {
      setGrid(getGridButtonForScope(mobileQuery.matches ? 'mobile' : 'desktop'));
    };

    section.querySelectorAll('.grid-selector-wrap').forEach((button) => {
      button.addEventListener('click', () => setGrid(button));
    });

    section.addEventListener('click', (event) => {
      const quickViewButton = event.target.closest('[data-quick-view-trigger]');
      if (quickViewButton) {
        event.preventDefault();
        event.stopPropagation();
        openQuickView(quickViewButton);
        return;
      }

      if (event.target.closest('[data-quick-view-close]')) {
        event.preventDefault();
        closeQuickView();
      }
    });

    const prefetchQuickView = (event) => {
      const quickViewButton = event.target.closest('[data-quick-view-trigger]');
      if (!quickViewButton || !section.contains(quickViewButton)) return;

      const productUrl = quickViewButton.dataset.productUrl;
      if (productUrl) fetchProductSection(productUrl).catch(() => {});
    };

    section.addEventListener('pointerover', prefetchQuickView, { passive: true });
    section.addEventListener('focusin', prefetchQuickView);
    section.addEventListener('touchstart', prefetchQuickView, { passive: true });

    const filterTrigger = section.querySelector('[data-asc-filter-trigger]');
    filterTrigger?.addEventListener('click', openDrawer);
    filterTrigger?.addEventListener('pointerenter', loadRemainingProducts, { passive: true, signal: lifecycleSignal });
    filterTrigger?.addEventListener('focus', loadRemainingProducts, { signal: lifecycleSignal });
    section.querySelectorAll('[data-filter-close]').forEach((button) => {
      button.addEventListener('click', closeDrawer);
    });

    section.querySelector('[data-filter-apply]')?.addEventListener('click', () => {
      closeDrawer();
    });

    clearButton?.addEventListener('click', () => {
      if (clearButton.disabled) return;

      draft = emptyState();
      state = cloneState(draft);
      updateOptionStates();
      updateProducts();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
        return;
      }

      if (event.key === 'Escape' && quickViewModal?.classList.contains('is-open')) {
        const nestedGuideOpen = quickViewModal.querySelector('.asc-pdp__modal:not([hidden])');
        if (!nestedGuideOpen) closeQuickView();
        return;
      }

      trapQuickViewFocus(event);
    }, { signal: lifecycleSignal });

    window.addEventListener('resize', () => {
      queueStickyOffset();
      queueDesktopCardAlignment();
    }, { passive: true, signal: lifecycleSignal });
    window.addEventListener('scroll', queueStickyOffset, { passive: true, signal: lifecycleSignal });
    window.addEventListener('load', queueStickyOffset, { once: true, signal: lifecycleSignal });
    window.visualViewport?.addEventListener('resize', queueStickyOffset, { passive: true, signal: lifecycleSignal });
    if (stickyHeader && 'ResizeObserver' in window) {
      stickyHeaderResizeObserver = new ResizeObserver(queueStickyOffset);
      stickyHeaderResizeObserver.observe(stickyHeader);
    }
    if (stickyHeader && 'MutationObserver' in window) {
      stickyHeaderMutationObserver = new MutationObserver(queueStickyOffset);
      stickyHeaderMutationObserver.observe(stickyHeader, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
    const handleResponsiveChange = () => {
      currentStickyOffset = -1;
      applyResponsiveGrid();
      queueStickyOffset();
      updateProducts();
    };

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleResponsiveChange, { signal: lifecycleSignal });
    } else {
      mobileQuery.addListener(handleResponsiveChange);
    }

    const isProductPageNearViewport = () => {
      if (!loadSentinel || !nextPageUrl) return false;

      const rect = loadSentinel.getBoundingClientRect();
      return rect.top <= window.innerHeight + 800 && rect.bottom >= -800;
    };

    const checkProductPagination = () => {
      productPageFrame = null;
      if (!isProductPageNearViewport()) return;

      loadNextProductPage()
        .then(() => queueProductPaginationCheck())
        .catch(() => {});
    };

    const queueProductPaginationCheck = () => {
      if (productPageFrame !== null) return;
      productPageFrame = window.requestAnimationFrame(checkProductPagination);
    };

    if (loadSentinel && nextPageUrl && 'IntersectionObserver' in window) {
      productPageObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        queueProductPaginationCheck();
      }, { rootMargin: '800px 0px' });
      productPageObserver.observe(loadSentinel);
    }

    window.addEventListener('scroll', queueProductPaginationCheck, { passive: true, signal: lifecycleSignal });
    window.addEventListener('pageshow', queueProductPaginationCheck, { passive: true, signal: lifecycleSignal });
    queueProductPaginationCheck();

    applyResponsiveGrid();
    setStickyOffset();
    renderFilterGroups();
    updateOptionStates();
    updateProducts();
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(section)) return;
      lifecycleController.abort();
      stickyHeaderResizeObserver?.disconnect();
      stickyHeaderMutationObserver?.disconnect();
      productPageObserver?.disconnect();
      window.cancelAnimationFrame(productPageFrame);
      window.cancelAnimationFrame(stickyFrame);
      window.cancelAnimationFrame(cardAlignmentFrame);
      window.clearTimeout(quickViewCloseTimer);
      quickViewRequest?.abort();
      document.body.classList.remove('asc-catalog-filter-open', 'asc-quick-view-open');
      document.documentElement.classList.remove('asc-quick-view-open');
      if (!mobileQuery.addEventListener) mobileQuery.removeListener(handleResponsiveChange);
      section.dataset.catalogControllerReady = 'false';
    }, { signal: lifecycleSignal });
    document.fonts?.ready.then(() => {
      queueStickyOffset();
      queueDesktopCardAlignment();
    });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-asc-catalog-root]')) initializeCatalog(scope);
    scope.querySelectorAll?.('[data-asc-catalog-root]').forEach(initializeCatalog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
